import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Activity,
  ArrowLeftRight,
  Hash,
  MessageCircle,
  Trophy,
  Users,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl } from '../utils/seo';
import { buildOgImageUrl } from '../utils/ogImage';
import { createCategorySlug } from '../utils/urlHelpers';
import { formatChangePercent } from '../utils/forumStatChange';
import type { ForumBase } from '../types/forum';

interface Props {
  darkMode: boolean;
}

interface CategoryStats {
  count: number;
  members: number;
  prevMembers: number;
  posts: number;
  prevPosts: number;
  topics: number;
  prevTopics: number;
}

function catStats(forums: ForumBase[], cat: string): CategoryStats {
  const list = forums.filter((f) => f.category === cat && f.is_active !== false);
  return {
    count: list.length,
    members: list.reduce((s, f) => s + f.members_count, 0),
    prevMembers: list.reduce((s, f) => s + (f.previous_month_members_count ?? 0), 0),
    posts: list.reduce((s, f) => s + f.posts_count, 0),
    prevPosts: list.reduce((s, f) => s + (f.previous_month_posts_count ?? 0), 0),
    topics: list.reduce((s, f) => s + f.topics_count, 0),
    prevTopics: list.reduce((s, f) => s + (f.previous_month_topics_count ?? 0), 0),
  };
}

type MetricKey = 'count' | 'members' | 'posts' | 'topics';

const METRICS: {
  key: MetricKey;
  label: string;
  icon: React.ReactNode;
  prevKey: 'prevMembers' | 'prevPosts' | 'prevTopics' | null;
}[] = [
  { key: 'count', label: 'Forum sayısı', icon: <Hash size={18} />, prevKey: null },
  { key: 'members', label: 'Toplam üye', icon: <Users size={18} />, prevKey: 'prevMembers' },
  { key: 'topics', label: 'Toplam konu', icon: <MessageCircle size={18} />, prevKey: 'prevTopics' },
  { key: 'posts', label: 'Toplam ileti', icon: <Activity size={18} />, prevKey: 'prevPosts' },
];

const CompareCategoriesPage: React.FC<Props> = ({ darkMode }) => {
  const [params, setParams] = useSearchParams();
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const a = params.get('a') || '';
  const b = params.get('b') || '';

  const pageSeo = useMemo(() => {
    if (a && b && a !== b) {
      return {
        title: `${a} vs ${b} — Kategori Karşılaştırma`,
        description: `${a} ve ${b} kategorilerinin forum, üye, konu ve ileti istatistikleri yan yana karşılaştırması.`,
        keywords: `${a}, ${b}, kategori karşılaştırma, forum istatistikleri`,
        canonical: absoluteUrl(
          `/compare-categories?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`
        ),
        ogImage: buildOgImageUrl({
          title: 'Kategori Karşılaştırma',
          subtitle: `${a} vs ${b}`,
          badge: 'Kategoriler',
        }),
      };
    }
    return {
      title: 'Kategori Karşılaştırma',
      description: 'İki forum kategorisinin toplam üye, ileti ve konu istatistiklerini karşılaştırın.',
      keywords: 'kategori karşılaştırma, forum',
      canonical: absoluteUrl('/compare-categories'),
    };
  }, [a, b]);

  usePageSeo(pageSeo, [a, b]);

  useEffect(() => {
    supabase
      .from('forums')
      .select('*')
      .eq('is_active', true)
      .then(({ data }) => {
        setForums(data || []);
        setCategories([...new Set((data || []).map((f) => f.category).filter(Boolean))].sort());
        setLoading(false);
      });
  }, []);

  const sa = a ? catStats(forums, a) : null;
  const sb = b ? catStats(forums, b) : null;
  const canCompare = !!(sa && sb && a !== b);

  const swapCategories = () => {
    if (!a || !b) return;
    const n = new URLSearchParams(params);
    n.set('a', b);
    n.set('b', a);
    setParams(n);
  };

  const setCategory = (side: 'a' | 'b', value: string) => {
    const n = new URLSearchParams(params);
    if (value) n.set(side, value);
    else n.delete(side);
    setParams(n);
  };

  const panel = darkMode ? 'bg-gray-800/90 border-gray-700' : 'bg-white border-gray-200 shadow-sm';
  const input = darkMode
    ? 'bg-gray-900/80 border-gray-600 text-white'
    : 'bg-gray-50 border-gray-200 text-gray-900';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  const accentA = darkMode
    ? 'from-indigo-900/60 to-indigo-800/30 border-indigo-700/60'
    : 'from-indigo-50 to-white border-indigo-200';
  const accentB = darkMode
    ? 'from-violet-900/60 to-violet-800/30 border-violet-700/60'
    : 'from-violet-50 to-white border-violet-200';

  if (loading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800' : 'bg-gradient-to-br from-blue-50 to-indigo-50'
        }`}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen ${
        darkMode ? 'bg-gradient-to-br from-gray-900 to-gray-800 text-gray-100' : 'bg-gradient-to-br from-blue-50 to-indigo-50 text-gray-900'
      }`}
    >
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-5xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} /> Anasayfaya dön
        </Link>

        <header className="text-center mb-10">
          <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-indigo-900'}`}>
            Kategori karşılaştırma
          </h1>
          <p className={`max-w-xl mx-auto ${muted}`}>
            İki kategoriyi seçin; forum, üye, konu ve ileti toplamlarını yan yana ve görsel olarak kıyaslayın.
          </p>
        </header>

        {/* Kategori seçici — simetrik 3 sütun */}
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-center mb-10">
          <div
            className={`rounded-2xl border bg-gradient-to-br p-5 ${accentA}`}
          >
            <label className="block">
              <span
                className={`text-xs font-semibold uppercase tracking-wide mb-2 block ${
                  darkMode ? 'text-indigo-300' : 'text-indigo-600'
                }`}
              >
                Kategori A
              </span>
              <select
                className={`rounded-xl border px-4 py-3 w-full text-base font-medium appearance-none cursor-pointer ${input} focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500`}
                value={a}
                onChange={(e) => setCategory('a', e.target.value)}
              >
                <option value="">Kategori seçin…</option>
                {categories.map((c) => (
                  <option key={c} value={c} disabled={c === b}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {a && (
              <Link
                to={`/category/${createCategorySlug(a)}`}
                className={`inline-block mt-3 text-sm font-medium ${
                  darkMode ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-800'
                }`}
              >
                {a} sayfasına git →
              </Link>
            )}
          </div>

          <div className="flex flex-col items-center justify-center gap-2 py-2 md:py-0">
            <span
              className={`w-14 h-14 rounded-full flex items-center justify-center text-sm font-bold border-2 ${
                darkMode
                  ? 'bg-gray-800 border-gray-600 text-gray-300'
                  : 'bg-white border-indigo-200 text-indigo-700 shadow-md'
              }`}
            >
              VS
            </span>
            {a && b && (
              <button
                type="button"
                onClick={swapCategories}
                title="Kategorileri yer değiştir"
                className={`flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                  darkMode
                    ? 'border-gray-600 text-gray-400 hover:bg-gray-700 hover:text-white'
                    : 'border-gray-200 text-gray-500 hover:bg-white hover:text-indigo-700'
                }`}
              >
                <ArrowLeftRight size={14} />
                Değiştir
              </button>
            )}
          </div>

          <div className={`rounded-2xl border bg-gradient-to-br p-5 ${accentB}`}>
            <label className="block">
              <span
                className={`text-xs font-semibold uppercase tracking-wide mb-2 block ${
                  darkMode ? 'text-violet-300' : 'text-violet-600'
                }`}
              >
                Kategori B
              </span>
              <select
                className={`rounded-xl border px-4 py-3 w-full text-base font-medium appearance-none cursor-pointer ${input} focus:ring-2 focus:ring-violet-500 focus:border-violet-500`}
                value={b}
                onChange={(e) => setCategory('b', e.target.value)}
              >
                <option value="">Kategori seçin…</option>
                {categories.map((c) => (
                  <option key={c} value={c} disabled={c === a}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            {b && (
              <Link
                to={`/category/${createCategorySlug(b)}`}
                className={`inline-block mt-3 text-sm font-medium ${
                  darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-800'
                }`}
              >
                {b} sayfasına git →
              </Link>
            )}
          </div>
        </div>

        {!canCompare ? (
          <div className={`rounded-2xl border p-10 text-center ${panel}`}>
            <Trophy className={`mx-auto mb-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} size={40} />
            <p className={`text-lg font-medium mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {a && b && a === b
                ? 'Lütfen birbirinden farklı iki kategori seçin.'
                : 'Karşılaştırmayı başlatmak için her iki kategoriyi de seçin.'}
            </p>
            <p className={`text-sm ${muted}`}>
              Seçimleriniz URL ile paylaşılabilir: ?a=KategoriAdı&b=DiğerKategori
            </p>
          </div>
        ) : (
          <ComparisonResults
            nameA={a}
            nameB={b}
            statsA={sa}
            statsB={sb}
            darkMode={darkMode}
            panel={panel}
          />
        )}
      </div>
    </div>
  );
};

function ComparisonResults({
  nameA,
  nameB,
  statsA,
  statsB,
  darkMode,
  panel,
}: {
  nameA: string;
  nameB: string;
  statsA: CategoryStats;
  statsB: CategoryStats;
  darkMode: boolean;
  panel: string;
}) {
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const rowBorder = darkMode ? 'border-gray-700/80' : 'border-gray-100';

  const winnerSummary = useMemo(() => {
    let winsA = 0;
    let winsB = 0;
    for (const m of METRICS) {
      const va = statsA[m.key];
      const vb = statsB[m.key];
      if (va > vb) winsA++;
      else if (vb > va) winsB++;
    }
    return { winsA, winsB };
  }, [statsA, statsB]);

  return (
    <div className="space-y-8">
      {/* Üst özet — iki sütun, ortalanmış */}
      <div className="grid sm:grid-cols-2 gap-4">
        <CategoryChip
          name={nameA}
          stats={statsA}
          wins={winnerSummary.winsA}
          side="a"
          darkMode={darkMode}
        />
        <CategoryChip
          name={nameB}
          stats={statsB}
          wins={winnerSummary.winsB}
          side="b"
          darkMode={darkMode}
        />
      </div>

      {/* Metrik karşılaştırma */}
      <div className={`rounded-2xl border overflow-hidden ${panel}`}>
        <div
          className={`grid grid-cols-2 gap-4 px-4 sm:px-8 py-5 border-b ${
            darkMode ? 'bg-gray-900/50 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="text-center sm:text-right min-w-0">
            <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
              Kategori A
            </p>
            <p className={`font-bold text-base sm:text-xl truncate ${darkMode ? 'text-white' : 'text-indigo-900'}`}>
              {nameA}
            </p>
          </div>
          <div className="text-center sm:text-left min-w-0">
            <p className={`text-xs uppercase tracking-wide font-semibold mb-1 ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
              Kategori B
            </p>
            <p className={`font-bold text-base sm:text-xl truncate ${darkMode ? 'text-white' : 'text-violet-900'}`}>
              {nameB}
            </p>
          </div>
        </div>

        <ul>
          {METRICS.map((m, idx) => (
            <MetricRow
              key={m.key}
              metric={m}
              valueA={statsA[m.key]}
              valueB={statsB[m.key]}
              prevA={m.prevKey ? statsA[m.prevKey] : 0}
              prevB={m.prevKey ? statsB[m.prevKey] : 0}
              darkMode={darkMode}
              isLast={idx === METRICS.length - 1}
              rowBorder={rowBorder}
            />
          ))}
        </ul>
      </div>
    </div>
  );
}

function CategoryChip({
  name,
  stats,
  wins,
  side,
  darkMode,
}: {
  name: string;
  stats: CategoryStats;
  wins: number;
  side: 'a' | 'b';
  darkMode: boolean;
}) {
  const isA = side === 'a';
  const gradient = isA
    ? darkMode
      ? 'from-indigo-900/50 to-gray-800 border-indigo-700/50'
      : 'from-indigo-50 to-white border-indigo-200'
    : darkMode
      ? 'from-violet-900/50 to-gray-800 border-violet-700/50'
      : 'from-violet-50 to-white border-violet-200';
  const titleColor = isA
    ? darkMode
      ? 'text-indigo-300'
      : 'text-indigo-700'
    : darkMode
      ? 'text-violet-300'
      : 'text-violet-700';

  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-5 text-center ${gradient}`}>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-1 ${titleColor}`}>
        {isA ? 'Kategori A' : 'Kategori B'}
      </p>
      <h2 className={`text-xl font-bold mb-3 truncate px-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        {name}
      </h2>
      <dl className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Forum</dt>
          <dd className={`font-bold text-lg tabular-nums ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats.count.toLocaleString('tr-TR')}
          </dd>
        </div>
        <div>
          <dt className={darkMode ? 'text-gray-500' : 'text-gray-500'}>Üye</dt>
          <dd className={`font-bold text-lg tabular-nums ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {stats.members.toLocaleString('tr-TR')}
          </dd>
        </div>
      </dl>
      {wins > 0 && (
        <p className={`mt-3 text-xs font-medium ${isA ? 'text-emerald-500' : 'text-emerald-500'}`}>
          {wins} metrikte önde
        </p>
      )}
    </div>
  );
}

function MetricRow({
  metric,
  valueA,
  valueB,
  prevA,
  prevB,
  darkMode,
  isLast,
  rowBorder,
}: {
  metric: (typeof METRICS)[number];
  valueA: number;
  valueB: number;
  prevA: number;
  prevB: number;
  darkMode: boolean;
  isLast: boolean;
  rowBorder: string;
}) {
  const max = Math.max(valueA, valueB, 1);
  const pctA = (valueA / max) * 100;
  const pctB = (valueB / max) * 100;
  const aWins = valueA > valueB;
  const bWins = valueB > valueA;
  const tie = valueA === valueB;

  const winA = darkMode ? 'text-emerald-300' : 'text-emerald-700';
  const winB = darkMode ? 'text-emerald-300' : 'text-emerald-700';
  const barA = darkMode ? 'bg-indigo-500' : 'bg-indigo-500';
  const barB = darkMode ? 'bg-violet-500' : 'bg-violet-500';
  const barBg = darkMode ? 'bg-gray-700' : 'bg-gray-100';
  const muted = darkMode ? 'text-gray-400' : 'text-gray-500';

  const growthA = metric.prevKey ? formatChangePercent(valueA, prevA) : '';
  const growthB = metric.prevKey ? formatChangePercent(valueB, prevB) : '';

  return (
    <li className={`px-4 sm:px-6 py-5 ${!isLast ? `border-b ${rowBorder}` : ''}`}>
      <div className="flex items-center justify-center gap-2 mb-4">
        <span className={darkMode ? 'text-indigo-400' : 'text-indigo-500'}>{metric.icon}</span>
        <span className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
          {metric.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:gap-8 max-w-lg sm:max-w-none mx-auto sm:mx-0">
        <div className="text-center sm:text-right">
          <p className={`text-xs mb-1 sm:hidden font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
            A {aWins && '↑'}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold tabular-nums mb-1 ${
              aWins ? winA : tie ? (darkMode ? 'text-white' : 'text-gray-900') : darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            {valueA.toLocaleString('tr-TR')}
          </p>
          {growthA && <p className={`text-xs mb-2 ${muted}`}>{growthA}</p>}
          <div className={`h-2.5 rounded-full overflow-hidden ${barBg} sm:ml-auto sm:max-w-[220px]`}>
            <div
              className={`h-full rounded-full ${barA} ml-auto`}
              style={{ width: `${pctA}%` }}
            />
          </div>
        </div>

        <div className="text-center sm:text-left">
          <p className={`text-xs mb-1 sm:hidden font-medium ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
            B {bWins && '↑'}
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold tabular-nums mb-1 ${
              bWins ? winB : tie ? (darkMode ? 'text-white' : 'text-gray-900') : darkMode ? 'text-gray-300' : 'text-gray-600'
            }`}
          >
            {valueB.toLocaleString('tr-TR')}
          </p>
          {growthB && <p className={`text-xs mb-2 ${muted}`}>{growthB}</p>}
          <div className={`h-2.5 rounded-full overflow-hidden ${barBg} sm:mr-auto sm:max-w-[220px]`}>
            <div className={`h-full rounded-full ${barB}`} style={{ width: `${pctB}%` }} />
          </div>
        </div>
      </div>

      <p className="hidden sm:block text-center mt-3">
        {aWins && <span className="text-xs font-medium text-emerald-500">Kategori A önde</span>}
        {bWins && <span className="text-xs font-medium text-emerald-500">Kategori B önde</span>}
        {tie && <span className={`text-xs ${muted}`}>Eşit</span>}
      </p>
    </li>
  );
}

export default CompareCategoriesPage;
